import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, message } from "../api";
import { SourceLink } from "../SourceLink";
import { agendaFileText } from "../agendaFile";

interface Utterance {
  id: string; speakerLabel: string | null;
  startMs: number; endMs: number; text: string; confidence: number | null;
  /** Who said this one line, when it isn't the voice's owner. */
  linePersonId: string | null; lineSource: "rollcall" | "manual" | null;
  lineConfirmed: boolean; lineReason: string | null;
}
interface RosterEntry { id: string; name: string; kind: "official" | "staff" | "other"; role: string; active: boolean }
interface Assignment {
  label: string; kind: "person" | "public" | "unknown" | "presenter" | "interpreter";
  personId: string | null; displayName: string | null;
  /** Presenters: position and organisation, and how they're addressed after. */
  role: string | null; honorific: string | null;
  /** Presenters and the public: set by the clerk, never guessed. */
  pronoun: "he" | "she" | null;
  source: "manual" | "suggested" | "voiceprint";
  confidence: number | null;
  evidence: { atMs: number | null; quote: string; reason: string }[];
  confirmed: boolean;
}
interface Segment {
  id: string; typeCode: string; typeName: string; status: string;
  startMs: number; endMs: number | null;
  items: { number: string; level: 1 | 2; title: string; paragraphs: string[] }[];
  metrics: Record<string, number | boolean> | null;
  hasDocument: boolean;
}
interface Detail {
  id: string; clientId: string; clientName: string; sourceUrl: string; title: string | null;
  agendaText: string | null;
  segments: Segment[];
  /** The meetings on the recording as the clerk set them, if they did. */
  segmentPlan: { meetingTypeId: string; startMs: number }[] | null;
  meetingTypes: { id: string; code: string; name: string; publicNaming: string }[];
  meetingDate: string | null; durationMs: number | null; hasAudio: boolean;
  /** Saved transcription results exist, so a rebuild costs nothing. */
  canRebuild: boolean;
  canRetranscribe: boolean;
  status: string; statusDetail: string | null;
  chapters: { startMs: number; endMs: number; label: string }[];
  speakers: { label: string; utterances: number; talkMs: number }[];
  utterances: Utterance[];
  roster: RosterEntry[];
  assignments: Assignment[];
  /** eligible: confirmed officials and staff here with no voiceprint yet. */
  voiceprints: { enabled: boolean; eligible: number; onFile: string[] };
}

const WORKING = new Set(["ingesting", "queued", "demuxing", "transcribing", "fusing", "identifying", "segmenting", "drafting", "enrolling"]);

export function clock(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

export function MeetingDetailPage({ isAdmin }: { isAdmin: boolean }) {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  /** The transcript line whose speaker is being corrected. */
  const [editingLine, setEditingLine] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement>(null);
  const [playerShown, setPlayerShown] = useState(false);

  const load = useCallback(async () => {
    try { setD(await api.get<Detail>(`/api/meetings/${id}`)); setError(null); }
    catch (err) { setError(message(err)); }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  // Follow the worker while it runs; stop once it has settled.
  useEffect(() => {
    if (!d || !WORKING.has(d.status)) return;
    const t = setInterval(() => void load(), 5_000);
    return () => clearInterval(t);
  }, [d, load]);

  const colour = useMemo(() => {
    const m = new Map<string, number>();
    d?.speakers.forEach((s, i) => m.set(s.label, i % 8));
    return m;
  }, [d]);

  const byLabel = useMemo(() => new Map((d?.assignments ?? []).map((a) => [a.label, a])), [d]);
  const personName = useMemo(() => new Map((d?.roster ?? []).map((r) => [r.id, r.name])), [d]);
  /** A person's colour is their voice's, where a voice is confirmed as them. */
  const personColour = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of d?.assignments ?? []) {
      if (a.confirmed && a.personId) m.set(a.personId, colour.get(a.label) ?? 0);
    }
    return m;
  }, [d, colour]);

  /** The longest thing each voice says: the best clip to recognise them by. */
  const sampleAt = useMemo(() => {
    const m = new Map<string, number>();
    const best = new Map<string, number>();
    for (const u of d?.utterances ?? []) {
      const k = u.speakerLabel ?? "unknown";
      const len = u.endMs - u.startMs;
      if (len > (best.get(k) ?? -1)) { best.set(k, len); m.set(k, u.startMs); }
    }
    return m;
  }, [d]);

  const nameOf = (a: Assignment | undefined): string | null => {
    if (!a) return null;
    if (a.kind === "person") return personName.get(a.personId ?? "") ?? "Unknown person";
    if (a.kind === "public") return a.displayName ? `${a.displayName} (public)` : "Member of the public";
    if (a.kind === "presenter") return `${a.displayName ?? "Presenter"} (presenter)`;
    if (a.kind === "interpreter") return a.displayName ? `${a.displayName} (interpreter)` : "Interpreter";
    return "Unknown speaker";
  };

  // When the audio link was fetched: it lasts two hours, and a clerk naming
  // voices can have the page open longer than that.
  const audioFetched = useRef(0);

  async function playAt(ms: number, retried = false) {
    const a = audio.current;
    if (!a) return;
    try {
      if (!a.src || Date.now() - audioFetched.current > 90 * 60_000) {
        const { url } = await api.get<{ url: string }>(`/api/meetings/${id}/audio`);
        a.src = url;
        audioFetched.current = Date.now();
        setPlayerShown(true);
      }
      const seek = () => {
        a.currentTime = ms / 1000;
        a.play().catch((err: unknown) => {
          // A blocked autoplay is the browser's; the controls still work.
          if (!(err instanceof DOMException && err.name === "NotAllowedError")) setError("The recording couldn't be played. Reload the page and try again.");
        });
      };
      // A link that failed to load (expired, say) is fetched again, once;
      // after that the clerk is told rather than left with silence.
      const ready = () => { a.removeEventListener("error", failed); seek(); };
      const failed = () => {
        a.removeEventListener("loadedmetadata", ready);
        if (retried) { setError("The recording couldn't be played in this browser. Reload the page and try again; if it still won't play, let us know."); return; }
        a.removeAttribute("src");
        audioFetched.current = 0;
        void playAt(ms, true);
      };
      if (a.readyState >= 1 && !a.error) { seek(); return; }
      a.addEventListener("loadedmetadata", ready, { once: true });
      a.addEventListener("error", failed, { once: true });
      a.load();
    } catch (err) { setError(message(err)); }
  }

  async function assign(label: string, body: object | null) {
    setSaving(label);
    try {
      if (body === null) await api.del(`/api/meetings/${id}/speakers/${encodeURIComponent(label)}`);
      else await api.put(`/api/meetings/${id}/speakers/${encodeURIComponent(label)}`, body);
      await load();
    } catch (err) { setError(message(err)); }
    finally { setSaving(null); }
  }

  /** Names one line only; null returns it to the voice's name. */
  async function assignLine(utteranceId: string, personId: string | null) {
    setSaving(utteranceId);
    try {
      if (personId === null) await api.del(`/api/utterances/${utteranceId}/speaker`);
      else await api.put(`/api/utterances/${utteranceId}/speaker`, { personId });
      setEditingLine(null);
      await load();
    } catch (err) { setError(message(err)); }
    finally { setSaving(null); }
  }

  async function reprocess(mode: "rebuild" | "retranscribe") {
    const ok = window.confirm(mode === "rebuild"
      ? "Rebuild this transcript from the saved results?\n\nNames confirmed for each voice are kept. " +
        "Corrections made to single lines will need redoing. Drafted minutes stay until you redraft."
      : "Transcribe this meeting again from the audio?\n\nThis is billed again (about $1–2), and the voices " +
        "are separated afresh, so every speaker name is cleared and must be confirmed again. " +
        "Drafted minutes stay until you redraft.");
    if (!ok) return;
    try { await api.post(`/api/meetings/${id}/reprocess`, { mode }); await load(); }
    catch (err) { setError(message(err)); }
  }

  async function acceptRollCall() {
    try { await api.post(`/api/meetings/${id}/lines/accept`); await load(); }
    catch (err) { setError(message(err)); }
  }

  async function draftMinutes() {
    try { await api.post(`/api/meetings/${id}/minutes/draft`); await load(); }
    catch (err) { setError(message(err)); }
  }

  async function download(segmentId: string) {
    try {
      const { url } = await api.get<{ url: string }>(`/api/segments/${segmentId}/docx`);
      window.location.href = url;
    } catch (err) { setError(message(err)); }
  }

  async function suggest() {
    try { await api.post(`/api/meetings/${id}/speakers/suggest`); await load(); }
    catch (err) { setError(message(err)); }
  }

  async function rememberVoices(n: number) {
    const ok = window.confirm(
      `Store voiceprints for ${n} confirmed ${n === 1 ? "official or staff member" : "officials and staff"}?\n\n` +
      "Future recordings will then suggest their names by voice. A voiceprint is biometric data: " +
      "only do this when you are sure of the names. Members of the public are never enrolled, " +
      "and any voiceprint can be deleted from the client page.");
    if (!ok) return;
    try { await api.post(`/api/meetings/${id}/voices/enrol`); await load(); }
    catch (err) { setError(message(err)); }
  }

  if (error && !d) return <div className="alert" role="alert">{error}</div>;
  if (!d) return <p className="muted">Loading…</p>;

  const chapters = [...d.chapters].sort((a, b) => a.startMs - b.startMs);
  let nextChapter = 0;
  const named = d.speakers.filter((s) => byLabel.get(s.label)?.confirmed).length;
  const editable = d.status === "review";
  // A meeting that failed while drafting still has its transcript: drafting again is the retry.
  const canDraft = editable || (d.status === "failed" && d.utterances.length > 0);
  const rollCallPending = d.utterances.filter((u) => u.lineSource === "rollcall" && !u.lineConfirmed && u.linePersonId).length;

  return (
    <>
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to="/meetings">Meetings</Link> <span aria-hidden="true">›</span>{" "}
        <Link to={`/clients/${d.clientId}`}>{d.clientName}</Link>
      </nav>
      <div className="pagehead">
        <div>
          <h1>{d.title ?? "Untitled recording"}</h1>
          <p className="muted small">
            {d.clientName}
            {" · "}<MeetingDate meetingId={d.id} value={d.meetingDate} onSaved={() => void load()} />
            {d.durationMs ? ` · ${clock(d.durationMs)}` : ""}
          </p>
          <p className="small"><SourceLink url={d.sourceUrl} /></p>
        </div>
        <Link className="linkbtn" to={`/clients/${d.clientId}`}>← {d.clientName}</Link>
      </div>

      {error && <div className="alert" role="alert">{error}</div>}

      <section className="card">
        <span className={`status status--${d.status}`}>{d.status}</span>
        {d.statusDetail && <span className="muted small"> — {d.statusDetail}</span>}
        {WORKING.has(d.status) && <p className="muted small">Processing; this page updates itself.</p>}
        {(d.status === "review" || d.status === "failed") && (d.canRebuild || (isAdmin && d.canRetranscribe)) && (
          <details className="reprocess">
            <summary className="small">Reprocess…</summary>
            <div className="reprocess__options">
              {d.canRebuild && (
                <div>
                  <button className="btn btn--inline btn--ghost btn--small" onClick={() => void reprocess("rebuild")}>
                    Rebuild from the saved transcript
                  </button>
                  <p className="hint">
                    Free. Rebuilds the lines, splits roll calls and suggests names again with the
                    latest improvements. Names you confirmed for a voice are kept.
                  </p>
                </div>
              )}
              {isAdmin && d.canRetranscribe && (
                <div>
                  <button className="btn btn--inline btn--ghost btn--small" onClick={() => void reprocess("retranscribe")}>
                    Transcribe again from the audio
                  </button>
                  <p className="hint">
                    Billed again (about $1–2). Speech-to-text and speaker separation from scratch —
                    useful once voiceprints are on file. Every name has to be confirmed again.
                  </p>
                </div>
              )}
            </div>
          </details>
        )}
      </section>

      {(canDraft || d.segments.length > 0) && d.speakers.length > 0 && (
        <section className="card">
          <div className="cardhead">
            <div>
              <h2>Minutes</h2>
              <p className="muted small">
                {d.segments.length === 0
                  ? "Drafted from the named transcript, in the client's own frame and house style. Name the voices first: the draft uses the names confirmed when you start it."
                  : "Everything the recording could not establish is left as a highlighted __ for the clerk to fill."}
              </p>
            </div>
            {canDraft && (
              <button className="btn btn--inline" onClick={() => void draftMinutes()}>
                {d.segments.length ? "Redraft minutes" : "Draft minutes"}
              </button>
            )}
          </div>
          <AgendaBox meetingId={d.id} text={d.agendaText} canEdit={canDraft} onSaved={() => void load()} />
          {d.segments.length > 0 && (
            <MeetingsOnRecording
              key={d.segments.map((s) => s.id).join()}
              meetingId={d.id} segments={d.segments} types={d.meetingTypes} durationMs={d.durationMs}
              setByClerk={Boolean(d.segmentPlan?.length)} canEdit={canDraft} onSaved={() => void load()}
            />
          )}
          {d.segments.map((sg) => <MinutesCard key={sg.id} seg={sg} clientId={d.clientId} isAdmin={isAdmin} onDownload={() => void download(sg.id)} />)}
        </section>
      )}

      {d.speakers.length > 0 && (
        <section className="card">
          <div className="cardhead">
            <div>
              <h2>Who's speaking <span className="muted">({named} of {d.speakers.length} named)</span></h2>
              <p className="muted small">
                Voices were separated automatically. Listen, then say who each one is.
                Suggestions come from voices on file and from cues in the transcript, with
                the quote they rest on.
              </p>
            </div>
            <div className="cardhead__actions">
              {editable && (
                <button className="btn btn--inline btn--ghost" onClick={() => void suggest()}>
                  Suggest names
                </button>
              )}
              {editable && isAdmin && d.voiceprints.enabled && d.voiceprints.eligible > 0 && (
                <button
                  className="btn btn--inline btn--ghost"
                  title="Store voiceprints for the officials and staff confirmed here"
                  onClick={() => void rememberVoices(d.voiceprints.eligible)}
                >
                  Remember voices ({d.voiceprints.eligible})
                </button>
              )}
            </div>
          </div>

          <table className="table speakers-table">
            <thead>
              <tr><th>Voice</th><th>Talk time</th><th></th><th>Who is this?</th></tr>
            </thead>
            <tbody>
              {d.speakers.map((s) => {
                const a = byLabel.get(s.label);
                const suggestion = a && !a.confirmed ? a : undefined;
                const current = a?.confirmed
                  ? (a.kind === "person" ? a.personId ?? "" : a.kind)
                  : "";
                return (
                  <tr key={s.label}>
                    <td>
                      <span className={`spk spk-${colour.get(s.label) ?? 0}`}>{s.label}</span>
                    </td>
                    <td className="muted small">{clock(s.talkMs)} · {s.utterances}</td>
                    <td>
                      {d.hasAudio && (
                        <button className="linkbtn" onClick={() => void playAt(sampleAt.get(s.label) ?? 0)}>
                          ▶ Listen
                        </button>
                      )}
                    </td>
                    <td>
                      <SpeakerPicker
                        value={current}
                        displayName={a?.confirmed && (a.kind === "public" || a.kind === "interpreter") ? a.displayName : null}
                        honorific={a?.confirmed && a.kind === "public" ? a.honorific : null}
                        naming={d.meetingTypes.some((t) => t.publicNaming !== "initial")
                          ? (d.meetingTypes.every((t) => t.publicNaming !== "initial") ? "full" : "mixed") : "initial"}
                        pronoun={a?.confirmed ? a.pronoun : null}
                        presenter={a?.confirmed && a.kind === "presenter" && a.displayName
                          ? { name: a.displayName, role: a.role, honorific: a.honorific } : null}
                        roster={d.roster}
                        disabled={!editable || saving === s.label}
                        onPick={(body) => void assign(s.label, body)}
                      />
                      {a?.confirmed && (
                        <span className="ok small"> ✓{a.source !== "manual" ? " accepted" : ""}</span>
                      )}
                      {a?.confirmed && a.personId && d.voiceprints.onFile.includes(a.personId) && (
                        <span className="muted small"> · voice on file</span>
                      )}
                      {suggestion && (
                        <div className="suggestion">
                          <span className="small">
                            {suggestion.source === "voiceprint" ? "Recognised by voice: " : "Suggested: "}
                            <strong>{nameOf(suggestion)}</strong>
                            {suggestion.kind === "presenter" && suggestion.role && <span> — {suggestion.role}</span>}
                            {suggestion.confidence !== null && <span className="muted"> · {suggestion.confidence}%</span>}
                          </span>
                          {suggestion.evidence.slice(0, 3).map((e, i) => (
                            <div key={i} className="evidence small">
                              {e.atMs !== null && (
                                <button className="linkbtn mono" onClick={() => void playAt(e.atMs!)}>
                                  {clock(e.atMs)}
                                </button>
                              )}{" "}
                              {e.quote ? <>“{e.quote}” <span className="muted">— {e.reason}</span></>
                                : <span className="muted">{e.reason}</span>}
                            </div>
                          ))}
                          {editable && (
                            <button
                              className="btn btn--inline btn--small"
                              disabled={saving === s.label}
                              onClick={() => void assign(s.label, suggestion.kind === "person"
                                ? { kind: "person", personId: suggestion.personId }
                                : suggestion.kind === "presenter"
                                  ? { kind: "presenter", displayName: suggestion.displayName ?? "", ...(suggestion.role ? { role: suggestion.role } : {}) }
                                  : suggestion.kind === "interpreter"
                                    ? { kind: "interpreter", ...(suggestion.displayName ? { displayName: suggestion.displayName } : {}) }
                                    : { kind: "public", displayName: suggestion.displayName ?? undefined })}
                            >
                              Accept
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {d.utterances.length > 0 && (
        <section className="card">
          <h2>Transcript <span className="muted">({d.utterances.length.toLocaleString()} utterances)</span></h2>
          {editable && (
            <p className="muted small">
              A name on a single line wrong? Click it to say who said that line only — the voice keeps its name. A member of the public, a presenter or an interpreter is named for the whole voice, under Who's speaking above.
            </p>
          )}
          {rollCallPending > 0 && (
            <div className="notice notice--inline rollcall-notice">
              <span>
                {rollCallPending} roll-call {rollCallPending === 1 ? "answer was" : "answers were"} attributed to
                the name the clerk called just before (marked with a dashed outline). Check them, then:
              </span>
              {editable && (
                <button className="btn btn--inline btn--small" onClick={() => void acceptRollCall()}>
                  Accept all {rollCallPending}
                </button>
              )}
            </div>
          )}
          <div className="transcript">
            {d.utterances.map((u) => {
              const headings: { startMs: number; label: string }[] = [];
              while (nextChapter < chapters.length && chapters[nextChapter]!.startMs <= u.startMs) {
                headings.push(chapters[nextChapter]!);
                nextChapter++;
              }
              const a = byLabel.get(u.speakerLabel ?? "");
              const line = u.linePersonId ? personName.get(u.linePersonId) ?? "Unknown person" : null;
              // A line-level name, confirmed or suggested, outranks the voice's.
              const name = line && u.lineConfirmed ? line : !line && a?.confirmed ? nameOf(a) : null;
              const guess = line && !u.lineConfirmed ? line : !line && a && !a.confirmed ? nameOf(a) : null;
              const hue = line ? personColour.get(u.linePersonId!) ?? colour.get(u.speakerLabel ?? "unknown") ?? 0
                : colour.get(u.speakerLabel ?? "unknown") ?? 0;
              const chipClass = `spk spk-${hue}${line ? " spk--line" : ""}${line && !u.lineConfirmed ? " spk--suggested" : ""}`;
              const chipTitle = line
                ? `${u.lineConfirmed ? "Set for this line only" : "Suggested for this line"}${u.lineReason ? ` — ${u.lineReason}` : ""} (voice ${u.speakerLabel ?? "?"})`
                : u.speakerLabel ?? undefined;
              const chipText = name ?? (guess ? `${guess}?` : u.speakerLabel ?? "?");
              return (
                <Fragment key={u.id}>
                  {headings.map((c) => (
                    <h3 key={`${c.startMs}-${c.label}`} className="chapter">
                      <span className="mono small muted">{clock(c.startMs)}</span> {c.label}
                    </h3>
                  ))}
                  <div className="utt">
                    <button
                      className="utt__time mono small linkbtn"
                      title="Play from here"
                      onClick={() => void playAt(u.startMs)}
                      disabled={!d.hasAudio}
                    >
                      {clock(u.startMs)}
                    </button>
                    <span className="utt__who">
                      {editable ? (
                        <button className={`${chipClass} spk--button`} title={chipTitle}
                                onClick={() => setEditingLine(editingLine === u.id ? null : u.id)}>
                          {chipText}
                        </button>
                      ) : <span className={chipClass} title={chipTitle}>{chipText}</span>}
                      {editable && line && !u.lineConfirmed && (
                        <button className="linkbtn small" title={`Accept: ${line} said this line`}
                                disabled={saving === u.id}
                                onClick={() => void assignLine(u.id, u.linePersonId)}>✓</button>
                      )}
                    </span>
                    <span className="utt__text">{u.text}</span>
                  </div>
                  {editingLine === u.id && (
                    <LinePicker
                      roster={d.roster}
                      current={u.lineConfirmed ? u.linePersonId : null}
                      voiceName={a?.confirmed ? nameOf(a) : null}
                      busy={saving === u.id}
                      onPick={(personId) => void assignLine(u.id, personId)}
                      onCancel={() => setEditingLine(null)}
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </section>
      )}

      {d.hasAudio && (
        // Mounted from the start, so the first click can play at once; shown
        // only once something has been loaded into it.
        <div className="audiobar" hidden={!playerShown}>
          <audio ref={audio} controls preload="none" />
        </div>
      )}
    </>
  );
}

/**
 * Who said one transcript line, whatever voice it was filed under. Only
 * rostered people: a line-level name exists for the one-word answers that
 * speaker separation misfiles, and those come from officials and staff.
 */
function LinePicker(props: {
  roster: RosterEntry[];
  current: string | null;
  voiceName: string | null;
  busy: boolean;
  onPick: (personId: string | null) => void;
  onCancel: () => void;
}) {
  const offered = props.roster.filter((r) => r.active || r.id === props.current);
  const officials = offered.filter((r) => r.kind === "official");
  const others = offered.filter((r) => r.kind !== "official");
  return (
    <div className="line-picker">
      <label className="small">
        Who said this line?{" "}
        <select
          autoFocus
          value={props.current ?? ""}
          disabled={props.busy}
          onChange={(e) => props.onPick(e.target.value === "" ? null : e.target.value)}
        >
          <option value="">{props.voiceName ? `The voice's name (${props.voiceName})` : "The voice's name"}</option>
          <optgroup label="Officials">
            {officials.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.role}</option>)}
          </optgroup>
          <optgroup label="Staff">
            {others.map((r) => <option key={r.id} value={r.id}>{r.name}{r.role ? ` — ${r.role}` : ""}</option>)}
          </optgroup>
        </select>
      </label>{" "}
      <button className="linkbtn small" onClick={props.onCancel} disabled={props.busy}>Cancel</button>
    </div>
  );
}

/** The surname, never a suffix: "Robert Smith Jr." is later "Mr. Smith". */
function surnameOf(name: string) {
  const words = name.trim().replace(/,/g, "").split(/\s+/);
  if (words.length > 1 && /^(?:Jr|Sr|II|III|IV|V)\.?$/i.test(words.at(-1)!)) words.pop();
  return words.at(-1) ?? "";
}

/**
 * Who a voice is: someone on the roster, a presenter (an invited speaker
 * who is not City staff), a member of the public, or unknown. Choosing a
 * rostered person saves straight away; the others ask for a few details.
 */
function SpeakerPicker(props: {
  value: string;
  displayName: string | null;
  /** Public only: Mr. / Ms. / Mx. / Dr., for a house that names the public in full. */
  honorific: string | null;
  /** How this client's meeting types name the public. */
  naming: "initial" | "full" | "mixed";
  pronoun: "he" | "she" | null;
  presenter: { name: string; role: string | null; honorific: string | null } | null;
  roster: RosterEntry[];
  disabled: boolean;
  onPick: (body: object | null) => void;
}) {
  const [pending, setPending] = useState<null | "public" | "presenter" | "interpreter">(null);
  const [publicName, setPublicName] = useState(props.displayName ?? "");
  const [publicHonorific, setPublicHonorific] = useState(props.honorific ?? "");
  useEffect(() => { setPublicHonorific(props.honorific ?? ""); }, [props.honorific]);
  const [name, setName] = useState(props.presenter?.name ?? "");
  const [role, setRole] = useState(props.presenter?.role ?? "");
  const [honorific, setHonorific] = useState(props.presenter?.honorific ?? "");
  const [pronoun, setPronoun] = useState<"he" | "she" | "">(props.pronoun ?? "");
  useEffect(() => { setPublicName(props.displayName ?? ""); }, [props.displayName]);
  useEffect(() => { setPronoun(props.pronoun ?? ""); }, [props.pronoun]);
  // Mr. and Ms. settle a presenter's pronoun unless the clerk says otherwise.
  const implied = /^mr\.?$/i.test(honorific) ? "he" : /^(ms|mrs|miss)\.?$/i.test(honorific) ? "she" : null;
  const pronounSelect = (label: string, auto: string) => (
    <select value={pronoun} aria-label={label} onChange={(e) => setPronoun(e.target.value as "he" | "she" | "")}>
      <option value="">{auto}</option>
      <option value="he">he</option>
      <option value="she">she</option>
    </select>
  );
  useEffect(() => {
    setName(props.presenter?.name ?? ""); setRole(props.presenter?.role ?? ""); setHonorific(props.presenter?.honorific ?? "");
  }, [props.presenter?.name, props.presenter?.role, props.presenter?.honorific]);

  // Deactivated people are offered only where they are already the answer.
  const offered = props.roster.filter((r) => r.active || r.id === props.value);
  const officials = offered.filter((r) => r.kind === "official");
  const staff = offered.filter((r) => r.kind !== "official");
  const former = (r: RosterEntry) => (r.active ? "" : " (former)");
  const mode = pending ?? (props.value === "public" || props.value === "presenter" || props.value === "interpreter" ? props.value : null);
  const publicImplied = /^mr\.?$/i.test(publicHonorific) ? "he" : /^(ms|mrs|miss)\.?$/i.test(publicHonorific) ? "she" : null;
  const laterForm = `${publicHonorific || "__"} ${surnameOf(publicName.trim() || "Shelby Somerville")}`;

  return (
    <span className="picker">
      <select
        value={pending ?? props.value}
        disabled={props.disabled}
        aria-label="Who is this?"
        onChange={(e) => {
          const v = e.target.value;
          if (v === "public" || v === "presenter" || v === "interpreter") { setPending(v); return; }
          setPending(null);
          if (v === "") props.onPick(null);
          else if (v === "unknown") props.onPick({ kind: "unknown" });
          else props.onPick({ kind: "person", personId: v });
        }}
      >
        <option value="">Not yet named</option>
        <optgroup label="Officials">
          {officials.map((r) => <option key={r.id} value={r.id}>{r.name}{former(r)} — {r.role}</option>)}
        </optgroup>
        <optgroup label="Staff">
          {staff.map((r) => <option key={r.id} value={r.id}>{r.name}{former(r)}{r.role ? ` — ${r.role}` : ""}</option>)}
        </optgroup>
        <option value="presenter">Presenter (not City staff)…</option>
        <option value="public">Member of the public…</option>
        <option value="interpreter">Interpreter…</option>
        <option value="unknown">Can't tell</option>
      </select>
      {mode === "public" && (
        <span className="picker__public">
          <input
            type="text"
            placeholder="First and last name, if given"
            value={publicName}
            maxLength={120}
            onChange={(e) => setPublicName(e.target.value)}
          />
          <button
            className="btn btn--inline btn--small"
            disabled={props.disabled}
            onClick={() => {
              setPending(null);
              props.onPick({
                kind: "public", displayName: publicName.trim() || undefined,
                ...(publicHonorific ? { honorific: publicHonorific } : {}), pronoun: pronoun || null,
              });
            }}
          >
            Save
          </button>
          {props.naming !== "initial" && (
            <select value={publicHonorific} aria-label="Honorific for this member of the public"
                    onChange={(e) => setPublicHonorific(e.target.value)}>
              <option value="">Honorific…</option>
              {["Mr.", "Ms.", "Mx.", "Dr."].map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          )}
          {pronounSelect("Pronoun for this member of the public",
            props.naming !== "initial" && publicImplied ? `Pronoun: ${publicImplied} (from ${publicHonorific})` : "Pronoun: none")}
          <span className="hint">
            {props.naming === "initial"
              ? <>Minutes use first name and last initial: "Shelby S." With no pronoun set, the name is repeated.</>
              : props.naming === "full"
                ? <>Minutes give the full name, then "{laterForm}".</>
                : <>Each meeting type names the public its own way: "Shelby S." in some, the full name and then "{laterForm}" in others.</>}
          </span>
        </span>
      )}
      {mode === "interpreter" && (
        <span className="picker__public">
          <input type="text" placeholder="Interpreter's name, if given" value={publicName} maxLength={120}
                 aria-label="Interpreter's name" onChange={(e) => setPublicName(e.target.value)} />
          <button
            className="btn btn--inline btn--small"
            disabled={props.disabled}
            onClick={() => {
              setPending(null);
              props.onPick({ kind: "interpreter", ...(publicName.trim() ? { displayName: publicName.trim() } : {}) });
            }}
          >
            Save
          </button>
          <span className="hint">
            What the interpreter says is written as the words of the person they interpret for, with one
            mention that they spoke through an interpreter.
          </span>
        </span>
      )}
      {mode === "presenter" && (
        <span className="picker__public picker__presenter">
          <input type="text" placeholder="Full name" value={name} maxLength={120}
                 aria-label="Presenter's full name" onChange={(e) => setName(e.target.value)} />
          <input type="text" placeholder="Position at organisation" value={role} maxLength={200}
                 aria-label="Presenter's position and organisation" onChange={(e) => setRole(e.target.value)} />
          <select value={honorific} aria-label="Presenter's honorific" onChange={(e) => setHonorific(e.target.value)}>
            <option value="">Honorific…</option>
            {["Mr.", "Ms.", "Mx.", "Dr."].map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          {pronounSelect("Presenter's pronoun", implied ? `Pronoun: ${implied} (from ${honorific})` : "Pronoun: none")}
          <button
            className="btn btn--inline btn--small"
            disabled={props.disabled || !name.trim()}
            onClick={() => {
              setPending(null);
              props.onPick({
                kind: "presenter", displayName: name.trim(),
                ...(role.trim() ? { role: role.trim() } : {}), ...(honorific ? { honorific } : {}),
                pronoun: pronoun || null,
              });
            }}
          >
            Save
          </button>
          <span className="hint">
            First mention: "{name.trim() || "Jane Doe"}, {role.trim() || "Program Manager at Hennepin County"}";
            then "{honorific || "__"} {surnameOf(name.trim() || "Jane Doe")}".
          </span>
        </span>
      )}
    </span>
  );
}


/** Shows every "__" blank highlighted, as it will be in Word, and *titles* in italics. */
function withBlanks(text: string) {
  const blanks = (t: string) => t.split(/(__+)/).map((part, i) =>
    /^__+$/.test(part) ? <mark key={i} className="blank">__</mark> : <Fragment key={i}>{part}</Fragment>);
  return text.split(/(?<![*\w])\*([^*\n]+?)\*(?![*\w])/).map((part, i) =>
    i % 2 === 1 ? <em key={i}>{blanks(part)}</em> : <Fragment key={i}>{blanks(part)}</Fragment>);
}

/** A paragraph of "| cell | cell |" lines, the first its headings. */
function MinutesTable({ text }: { text: string }) {
  const rows = text.split("\n").map((l) => l.trim()).filter((l) => /^\|.*\|$/.test(l) && !/^\|[\s:|-]+\|$/.test(l))
    .map((l) => l.slice(1, -1).split("|").map((c) => c.trim()));
  return (
    <table className="minutes__table">
      <thead><tr>{rows[0]!.map((c, i) => <th key={i}>{withBlanks(c)}</th>)}</tr></thead>
      <tbody>{rows.slice(1).map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{withBlanks(c)}</td>)}</tr>)}</tbody>
    </table>
  );
}

/**
 * The posted agenda. Stations do not always mark a recording's items; when
 * they have not, this is what gives the minutes the city's own numbering.
 */
/**
 * The meeting's date, which goes on the minutes' title block, page header
 * and file name. Changing it re-dates any Word files already drafted.
 */
function MeetingDate(props: { meetingId: string; value: string | null; onSaved: () => void }) {
  // The day as the clerk's own calendar shows it (a recording stamped at 7
  // p.m. Central is already the next day in UTC).
  const local = props.value ? new Date(props.value) : null;
  const pad = (n: number) => String(n).padStart(2, "0");
  const day = local ? `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}` : "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(day);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setValue(day); }, [day]);

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/meetings/${props.meetingId}/date`, { date: value || null });
      setEditing(false);
      props.onSaved();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <>
        {props.value
          ? new Date(props.value).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
          : <span className="warn">no meeting date</span>}{" "}
        <button className="linkbtn small" onClick={() => setEditing(true)}>{props.value ? "Change" : "Set the date"}</button>
      </>
    );
  }
  return (
    <span className="inline-edit">
      <input type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Meeting date" />
      <button className="btn btn--inline btn--small" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : "Save"}</button>
      <button className="linkbtn small" onClick={() => { setEditing(false); setValue(day); }}>Cancel</button>
      {error && <span className="warn small"> {error}</span>}
    </span>
  );
}

function AgendaBox(props: { meetingId: string; text: string | null; canEdit: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(props.text ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setText(props.text ?? ""); }, [props.text]);
  const dirty = text.trim() !== (props.text ?? "").trim();

  async function readFile(file: File) {
    setReading(true); setError(null);
    try { setText(await agendaFileText(props.meetingId, file)); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setReading(false); if (fileRef.current) fileRef.current.value = ""; }
  }
  const lines = (props.text ?? "").split("\n").filter((l) => l.trim()).length;
  if (!props.canEdit && !props.text) return null;

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/meetings/${props.meetingId}/agenda`, { text: text.trim() ? text : null });
      props.onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="agenda">
      <button className="linkbtn" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Hide" : props.text ? "Posted agenda" : "Add the posted agenda"}
      </button>
      {!open && <span className="muted small"> {props.text
        ? `${lines} ${lines === 1 ? "line" : "lines"} on file — the minutes use its numbering and titles.`
        : "Paste it and the minutes follow its numbering and titles exactly."}</span>}
      {open && (
        <>
          <p className="muted small">
            Paste the agenda as posted, or choose the agenda file (PDF or Word) and its text is put in the box for
            you to check. Items keep their numbers and letters (<span className="mono">4. Consent Agenda</span>,{" "}
            <span className="mono">A. City Council Minutes</span>). If this recording holds several meetings, put each
            agenda under a line naming its meeting — <span className="mono">Study Session</span>,{" "}
            <span className="mono">Regular Session</span>, <span className="mono">HRA</span>. Used the next time the
            minutes are drafted; its date becomes the meeting's date if none is set.
          </p>
          {error && <div className="alert" role="alert">{error}</div>}
          {props.canEdit ? (
            <>
              <input ref={fileRef} type="file" hidden accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) void readFile(f); }} />
              <div className="actions">
                <button className="btn btn--inline btn--small btn--ghost" type="button" disabled={reading}
                        onClick={() => fileRef.current?.click()}>
                  {reading ? "Reading the agenda…" : "Choose the agenda file"}
                </button>
              </div>
              <textarea className="notes-box" rows={10} value={text} onChange={(e) => setText(e.target.value)}
                        aria-label="Posted agenda" />
              <div className="actions">
                <button className="btn btn--inline btn--small" disabled={busy || !dirty} onClick={() => void save()}>
                  {busy ? "Saving…" : "Save agenda"}
                </button>
                {!dirty && props.text && <span className="muted small">Saved. Redraft to apply it.</span>}
              </div>
            </>
          ) : <pre className="notes-box notes-box--read">{props.text}</pre>}
        </>
      )}
    </div>
  );
}

/** "1:08:32" or "8:32" as milliseconds; null if it is neither. */
function parseClock(text: string): number | null {
  const m = /^\s*(?:(\d{1,2}):)?(\d{1,2}):(\d{2})\s*$/.exec(text);
  if (!m || Number(m[3]) > 59 || (m[1] !== undefined && Number(m[2]) > 59)) return null;
  return ((Number(m[1] ?? 0) * 60 + Number(m[2])) * 60 + Number(m[3])) * 1000;
}

/**
 * Which meetings the recording holds, and where each starts. The portal
 * works it out when drafting; when it gets it wrong -- one long council
 * meeting taken for three -- the clerk says so here and the minutes are
 * redrafted that way.
 */
function MeetingsOnRecording(props: {
  meetingId: string; segments: Segment[]; types: Detail["meetingTypes"]; durationMs: number | null;
  setByClerk: boolean; canEdit: boolean; onSaved: () => void;
}) {
  const fallback = props.types[0]?.id ?? "";
  const initial = props.segments.map((s, i) => ({
    meetingTypeId: props.types.find((t) => t.code === s.typeCode)?.id ?? fallback,
    start: i === 0 ? "0:00" : clock(s.startMs),
  }));
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summary = props.segments.map((s) => `${s.typeName} from ${clock(s.startMs)}`).join(", ");

  const set = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const times = rows.map((r, i) => (i === 0 ? 0 : parseClock(r.start)));
  const problem = times.some((t) => t === null) ? "Write each start time as h:mm:ss, such as 1:42:10."
    : times.some((t, i) => i > 0 && t! <= times[i - 1]!) ? "Each meeting must start after the one before it."
      : props.durationMs !== null && times.some((t) => t! >= props.durationMs!) ? `The recording is only ${clock(props.durationMs)} long.`
        : null;

  async function save(plan: { meetingTypeId: string; startMs: number }[] | null) {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/meetings/${props.meetingId}/segment-plan`, { plan });
      props.onSaved();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="agenda">
      <button className="linkbtn" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? "Hide" : props.segments.length === 1 ? "One meeting on this recording" : `${props.segments.length} meetings on this recording`}
      </button>
      {!open && <span className="muted small"> {summary}{props.setByClerk ? " — as you set them." : ". Wrong? Change it here."}</span>}
      {open && (
        <>
          <p className="muted small">
            Say which meetings the recording holds and where each one is called to order, then redraft. A recess is
            not a new meeting. The times are the ones in the transcript.
          </p>
          {error && <div className="alert" role="alert">{error}</div>}
          <table className="table">
            <thead><tr><th>Meeting</th><th>Starts at</th><th></th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <select value={r.meetingTypeId} disabled={!props.canEdit} aria-label="Meeting type"
                            onChange={(e) => set(i, { meetingTypeId: e.target.value })}>
                      {props.types.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
                    </select>
                  </td>
                  <td>
                    {i === 0
                      ? <span className="mono">0:00</span>
                      : <input type="text" className="mono" size={9} value={r.start} disabled={!props.canEdit}
                               aria-label="Start time" placeholder="1:42:10" onChange={(e) => set(i, { start: e.target.value })} />}
                  </td>
                  <td>
                    {props.canEdit && rows.length > 1 && (
                      <button className="linkbtn small" onClick={() => setRows(rows.filter((_, j) => j !== i))}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {props.canEdit && (
            <div className="actions">
              <button className="btn btn--inline btn--small btn--ghost"
                      onClick={() => setRows([...rows, { meetingTypeId: fallback, start: "" }])}>
                Add a meeting
              </button>
              {rows.length > 1 && (
                <button className="btn btn--inline btn--small btn--ghost" onClick={() => setRows(rows.slice(0, 1))}>
                  It's all one meeting
                </button>
              )}
              <button className="btn btn--inline btn--small" disabled={busy || problem !== null}
                      onClick={() => void save(rows.map((r, i) => ({ meetingTypeId: r.meetingTypeId, startMs: times[i]! })))}>
                {busy ? "Starting…" : "Redraft as these meetings"}
              </button>
              {props.setByClerk && (
                <button className="linkbtn small" disabled={busy} onClick={() => void save(null)}>
                  Let the portal work it out again
                </button>
              )}
            </div>
          )}
          {props.canEdit && problem && <p className="muted small">{problem}</p>}
          <p className="muted small">Redrafting replaces the drafts below; names and the transcript are kept.</p>
        </>
      )}
    </div>
  );
}

/** Base64 of a file, for a JSON upload. */
async function fileBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/**
 * The clerk's corrected minutes in; the style rules for that kind of
 * meeting out, revised as one consistent set, for the clerk to read and save.
 */
interface Revision { revised: string; changes: string; summary: string; current: string; typeId: string; typeName: string }

function TeachEdits({ segmentId, onClose }: { segmentId: string; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Revision | null>(null);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);

  async function read(file: File) {
    setError(null); setResult(null); setSaved(false);
    if (!/[.]docx$/i.test(file.name)) { setError("Choose the corrected minutes as a Word .docx file."); return; }
    setBusy(true);
    try {
      const r = await api.post<Revision>(`/api/segments/${segmentId}/learn-from-edits`, { docxBase64: await fileBase64(file) });
      setResult(r); setText(r.revised);
    } catch (err) {
      setError(err instanceof ApiError && typeof err.body.message === "string" ? err.body.message : message(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!result || !text.trim()) return;
    setBusy(true); setError(null);
    try { await api.patch(`/api/meeting-types/${result.typeId}`, { promptNotes: text }); setSaved(true); }
    catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  return (
    <div className="learned">
      <p className="small">
        <strong>Teach it your edits.</strong> Choose your corrected version of these minutes (.docx). The portal
        compares it with the draft and revises the style rules for this kind of meeting into one consistent set,
        keeping what still holds and correcting what your edits contradict. One-off fixes, like a misheard name,
        are left out. Nothing changes until you save, and your file is not kept.
      </p>
      <p className="small muted">
        Layout problems (where headings or lines fall, dates, numbering) are not style rules: send those to us.
      </p>
      <input ref={fileRef} type="file" hidden
             accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
             onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); }} />
      {error && <div className="alert" role="alert">{error}</div>}
      {!result && (
        <div className="actions">
          <button className="btn btn--inline btn--small" disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? "Comparing… (about a minute)" : "Choose the corrected minutes"}
          </button>
          <button className="linkbtn" onClick={onClose}>Cancel</button>
        </div>
      )}
      {result && (saved ? (
        <div className="notice notice--inline" role="status">
          Style rules for {result.typeName} saved. They apply from the next draft.{" "}
          <Link to={`/meeting-types/${result.typeId}`}>See the rules</Link>{" "}
          <button className="linkbtn" onClick={onClose}>Close</button>
        </div>
      ) : (
        <>
          {result.summary && <p className="small">{result.summary}</p>}
          {result.changes && (
            <>
              <p className="small"><strong>What changes:</strong></p>
              <pre className="notes-box notes-box--read small">{result.changes}</pre>
            </>
          )}
          <p className="small">The revised style rules for <strong>{result.typeName}</strong> — read them through and edit as needed:</p>
          <textarea className="notes-box" rows={16} value={text} onChange={(e) => setText(e.target.value)}
                    aria-label="Revised style rules" />
          <div className="actions">
            <button className="btn btn--inline btn--small" disabled={busy || !text.trim()} onClick={() => void save()}>
              {busy ? "Saving…" : `Replace the ${result.typeName} style rules with these`}
            </button>
            <button className="linkbtn" onClick={() => setShowCurrent((v) => !v)}>{showCurrent ? "Hide" : "Show"} the current rules</button>
            <button className="linkbtn" onClick={onClose}>Discard</button>
          </div>
          {showCurrent && <pre className="notes-box notes-box--read small">{result.current || "(none)"}</pre>}
        </>
      ))}
    </div>
  );
}

function MinutesCard({ seg, clientId, isAdmin, onDownload }: {
  seg: Segment; clientId: string; isAdmin: boolean; onDownload: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [teaching, setTeaching] = useState(false);
  const m = seg.metrics;
  const n = (k: string) => (typeof m?.[k] === "number" ? (m[k] as number) : null);
  const words = n("words");
  const noPronoun = n("namesWithoutPronoun") ?? 0;
  return (
    <div className="minutes">
      <div className="minutes__head">
        <div>
          <strong>{seg.typeName}</strong> <span className="pill pill--quiet">{seg.typeCode}</span>
          <span className="muted small"> {clock(seg.startMs)}–{seg.endMs !== null ? clock(seg.endMs) : "end"} · {seg.status}</span>
          {m && (
            <div className="muted small">
              {words !== null && <>~{(words / 450).toFixed(1)} pages · </>}
              {n("placeholders")} blanks to fill · {n("motionsVerified")} of {n("motions")} motions verified
              {m.clockStated === false && <> · clock times need checking</>}
            </div>
          )}
          {noPronoun > 0 && (
            <div className="small warn">
              {noPronoun} {noPronoun === 1 ? "person named here has" : "people named here have"} no pronoun set, so
              {noPronoun === 1 ? " that name is" : " their names are"} repeated where "He" or "She" would read better.{" "}
              <Link to={`/clients/${clientId}`}>Set pronouns</Link>, then redraft.
            </div>
          )}
          {!seg.hasDocument && seg.items.length > 0 && (
            <div className="small muted">No Word file: this meeting type has no template on file yet.</div>
          )}
        </div>
        <div className="actions">
          {seg.hasDocument && <button className="btn btn--inline btn--small" onClick={onDownload}>Download .docx</button>}
          {seg.items.length > 0 && (
            <button className="linkbtn" onClick={() => setOpen((v) => !v)}>{open ? "Hide" : "Preview"}</button>
          )}
          {isAdmin && seg.items.length > 0 && !teaching && (
            <button className="linkbtn" onClick={() => setTeaching(true)}>Teach it my edits</button>
          )}
        </div>
      </div>
      {teaching && <TeachEdits segmentId={seg.id} onClose={() => setTeaching(false)} />}
      {open && (
        <div className="minutes__body">
          {seg.items.map((it, i) => (
            <Fragment key={`${i}-${it.number}`}>
              <h4 className={`minutes__h minutes__h--${it.level}`}>{it.number ? <>{it.number}.&emsp;</> : null}{it.title}</h4>
              {it.paragraphs.map((p, i) => {
                // A bold line heads the paragraph after it, as in Word.
                const bold = /^\*\*([\s\S]+)\*\*$/.exec(p);
                const lines = p.split("\n").filter((l) => l.trim());
                if (lines.length >= 2 && lines.every((l) => /^\s*\|.*\|\s*$/.test(l))) return <MinutesTable key={i} text={p} />;
                return bold
                  ? <p key={i} className="minutes__boldline"><strong>{withBlanks(bold[1]!)}</strong></p>
                  : <p key={i}>{withBlanks(p)}</p>;
              })}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
